import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, BarChart3, FileCheck, Search, GraduationCap, Plus, ChevronDown, ArrowRight, CheckCircle } from 'lucide-react';

const ServicesPage = () => {
  const [expandedService, setExpandedService] = useState(null);

  const services = [
    {
      id: 'consultoria',
      icon: Shield,
      title: 'Consultoria em Segurança da Informação & Cybersegurança',
      summary: 'Criação de políticas, arquitetura de segurança, gestão de acessos, monitoramento e resposta a incidentes.',
      color: 'from-blue-500 to-cyan-500',
      details: [
        'Criação e revisão de políticas, normas e procedimentos de segurança da informação',
        'Design de arquiteturas de segurança alinhadas ao negócio',
        'Implementação de controles de acesso (IAM, PAM, gestão de privilégios)',
        'Estruturação de SOC (Security Operations Center) e processos de monitoramento',
        'Resposta a incidentes: playbooks, investigações, contenção e remediação',
        'Hardening de sistemas, servidores e aplicações',
        'Segurança em ambientes cloud (AWS, Azure, GCP)'
      ],
      benefits: 'Redução de superfície de ataque, resposta rápida a incidentes, proteção de ativos críticos e alinhamento com melhores práticas globais.'
    },
    {
      id: 'grc',
      icon: BarChart3,
      title: 'Governança, Risco e Compliance (GRC)',
      summary: 'Estruturação de modelos de governança, gestão de riscos corporativos e tecnológicos, definição de KPIs e KRIs.',
      color: 'from-emerald-500 to-teal-500',
      details: [
        'Estruturação de modelos de governança de TI e segurança (COBIT, ITIL)',
        'Gestão de riscos corporativos e tecnológicos: identificação, análise, tratamento e monitoramento',
        'Definição de KPIs (Key Performance Indicators) e KRIs (Key Risk Indicators)',
        'Implementação de frameworks de compliance (SOX, BACEN, SUSEP, LGPD)',
        'Auditorias internas e preparação para auditorias externas',
        'Estruturação de comitês de riscos e compliance',
        'Dashboard executivo para visibilidade estratégica de riscos'
      ],
      benefits: 'Visão consolidada de riscos, tomada de decisão baseada em dados, redução de custos com não conformidades e alinhamento regulatório.'
    },
    {
      id: 'iso27001',
      icon: FileCheck,
      title: 'ISO 27001 & Conformidade',
      summary: 'Gap analysis completa, roadmap de implementação e suporte na certificação.',
      color: 'from-violet-500 to-purple-500',
      details: [
        'Gap analysis detalhado dos 93 controles da ISO 27001:2022',
        'Roadmap de implementação com priorização baseada em riscos',
        'Criação de políticas, procedimentos e evidências documentais',
        'Suporte na definição de escopo e Declaração de Aplicabilidade (SoA)',
        'Preparação para auditorias de Stage 1 e Stage 2',
        'Treinamento de equipes internas sobre requisitos da norma',
        'Suporte pós-certificação para manutenção e auditorias de vigilância'
      ],
      benefits: 'Certificação reconhecida internacionalmente, demonstração de compromisso com segurança, vantagem competitiva em licitações e contratos.'
    },
    {
      id: 'pentest',
      icon: Search,
      title: 'Pentest & Forense Digital',
      summary: 'Testes de penetração em aplicações, infraestrutura e cloud, simulações de ataques e investigação de incidentes.',
      color: 'from-orange-500 to-red-500',
      details: [
        'Pentest em aplicações web e mobile (OWASP Top 10)',
        'Pentest em infraestrutura de redes, servidores e dispositivos',
        'Pentest em ambientes cloud (AWS, Azure, GCP)',
        'Red Team: simulações de ataques reais com objetivos específicos',
        'Forense digital: coleta, preservação e análise de evidências',
        'Investigação de incidentes de segurança e vazamentos de dados',
        'Relatórios executivos e técnicos detalhados com priorização de remediação'
      ],
      benefits: 'Identificação proativa de vulnerabilidades, validação da eficácia dos controles, evidências concretas para decisões de investimento em segurança.'
    },
    {
      id: 'treinamentos',
      icon: GraduationCap,
      title: 'Conscientização e Treinamentos',
      summary: 'Programas de awareness contínuo, simulações de phishing e relatórios de engajamento.',
      color: 'from-pink-500 to-rose-500',
      details: [
        'Programas de conscientização contínua em segurança da informação',
        'Campanhas de phishing simulado com métricas de engajamento',
        'Treinamentos customizados por público-alvo (executivos, TI, usuários finais)',
        'Workshops sobre LGPD, gestão de incidentes e boas práticas',
        'Gamificação e conteúdos interativos para maior retenção',
        'Relatórios de engajamento e evolução comportamental',
        'Certificações de conclusão e trilhas de aprendizado'
      ],
      benefits: 'Redução de incidentes causados por erro humano, cultura organizacional de segurança, conformidade com requisitos regulatórios de treinamento.'
    },
    {
      id: 'outros',
      icon: Plus,
      title: 'Outros Serviços Especializados',
      summary: 'TPRM, DPIA, Vulnerability Management e soluções customizadas.',
      color: 'from-slate-500 to-gray-600',
      details: [
        'TPRM (Third-Party Risk Management): avaliação e gestão de riscos de terceiros',
        'DPIA (Data Protection Impact Assessment): análise de impacto à proteção de dados',
        'Vulnerability Management: gestão contínua de vulnerabilidades com priorização',
        'BIA (Business Impact Analysis): análise de impacto ao negócio',
        'Continuidade de negócios e Disaster Recovery',
        'Soluções customizadas conforme necessidades específicas do cliente'
      ],
      benefits: 'Cobertura completa de necessidades de segurança e compliance, flexibilidade para atender demandas específicas do seu setor.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Serviços - DMM Security | Consultoria em Segurança e GRC</title>
        <meta name="description" content="Serviços completos de consultoria em segurança da informação, GRC, ISO 27001, Pentest, Forense Digital e Treinamentos. Soluções personalizadas para sua empresa." />
      </Helmet>

      <div className="min-h-screen pt-20" data-testid="services-page">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-[#0f2535] via-[#1a3a52] to-[#1e4d6b] text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `linear-gradient(rgba(16,185,129,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(16,185,129,0.2) 1px, transparent 1px)`,
              backgroundSize: '60px 60px'
            }} />
          </div>
          
          <div className="container mx-auto px-4 relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto text-center"
            >
              <span className="inline-block px-4 py-1.5 bg-[#10b981]/20 text-[#10b981] text-sm font-semibold rounded-full mb-6">
                Nossos Serviços
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                Soluções Completas em Segurança
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed">
                Serviços personalizados para elevar a maturidade de segurança e compliance da sua organização
              </p>
            </motion.div>
          </div>
        </section>

        {/* Services List */}
        <section className="py-20 bg-white" data-testid="services-list">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto space-y-6">
              {services.map((service, index) => (
                <motion.div
                  key={service.id}
                  id={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300"
                  data-testid={`service-${service.id}`}
                >
                  <div
                    className="p-8 cursor-pointer group"
                    onClick={() => setExpandedService(expandedService === service.id ? null : service.id)}
                  >
                    <div className="flex items-start gap-6">
                      <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                        <service.icon className="w-8 h-8 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h2 className="text-2xl font-bold text-[#1a3a52] mb-2 group-hover:text-[#10b981] transition-colors">
                              {service.title}
                            </h2>
                            <p className="text-gray-600">{service.summary}</p>
                          </div>
                          <button className="text-[#1a3a52] flex-shrink-0 p-2 hover:bg-gray-100 rounded-lg transition-colors">
                            <motion.div
                              animate={{ rotate: expandedService === service.id ? 180 : 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              <ChevronDown className="w-6 h-6" />
                            </motion.div>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <AnimatePresence>
                    {expandedService === service.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="border-t border-gray-100 bg-gray-50 p-8">
                          <h3 className="text-lg font-bold text-[#1a3a52] mb-6">O que entregamos:</h3>
                          <ul className="space-y-3 mb-8">
                            {service.details.map((detail, idx) => (
                              <motion.li 
                                key={idx} 
                                className="flex items-start gap-3"
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: idx * 0.05 }}
                              >
                                <CheckCircle className="w-5 h-5 text-[#10b981] flex-shrink-0 mt-0.5" />
                                <span className="text-gray-700">{detail}</span>
                              </motion.li>
                            ))}
                          </ul>

                          <div className="bg-gradient-to-r from-[#10b981]/10 to-emerald-50 border border-[#10b981]/20 rounded-xl p-6">
                            <h4 className="font-bold text-[#1a3a52] mb-2 flex items-center gap-2">
                              <ArrowRight className="w-5 h-5 text-[#10b981]" />
                              Benefícios
                            </h4>
                            <p className="text-gray-700">{service.benefits}</p>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-gradient-to-br from-[#1a3a52] to-[#10b981] text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 30% 70%, rgba(255,255,255,0.1) 0%, transparent 50%)`
            }} />
          </div>
          
          <div className="container mx-auto px-4 text-center relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Precisa de uma Solução Customizada?
              </h2>
              <p className="text-xl mb-10 text-gray-200">
                Cada organização é única. Vamos construir juntos a solução ideal para seus desafios de segurança e compliance.
              </p>
              <Link
                to="/contato"
                className="group inline-flex items-center gap-2 bg-white text-[#1a3a52] px-10 py-5 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all duration-300 hover:shadow-2xl hover:scale-105"
                data-testid="services-cta-btn"
              >
                Falar com Especialista
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ServicesPage;
